import { useState, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, Cell } from 'recharts';
import { api } from '../services/api';
import DateRangePicker from '../components/DateRangePicker';
import InfoPanel from '../components/InfoPanel';
import { useDateRange } from '../hooks/useDateRange';
import { toCsv, downloadCsv, rangeSuffix } from '../utils/csv';

const COLORS = [
  '#3b82f6', '#ef4444', '#f59e0b', '#10b981', '#8b5cf6',
  '#ec4899', '#06b6d4', '#84cc16', '#f97316', '#6366f1',
  '#14b8a6', '#a855f7', '#22c55e', '#eab308', '#64748b',
  '#0ea5e9', '#d946ef', '#f43f5e', '#78716c', '#7c3aed',
];

export default function AuthorViews() {
  const { fromDate, toDate, setFromDate, setToDate, from, to } = useDateRange();
  const [searchTerm, setSearchTerm] = useState('');

  const { data: allAuthors, isLoading } = useQuery({
    queryKey: ['allAuthors', from, to],
    queryFn: () => api.getAllAuthors(from, to),
  });

  const filteredAuthors = useMemo(() => {
    if (!allAuthors?.authors) return [];
    if (!searchTerm) return allAuthors.authors;
    return allAuthors.authors.filter((author) =>
      author.authors.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [allAuthors, searchTerm]);

  const barChartData = useMemo(() => {
    if (!allAuthors?.authors) return [];
    return allAuthors.authors.slice(0, 20).map((author) => ({
      name: author.authors.length > 35 ? author.authors.substring(0, 35) + '...' : author.authors,
      fullName: author.authors,
      value: Number(author.view_count),
    }));
  }, [allAuthors]);

  // Channel totals span all authors, not the filtered set, so the app
  // apportionment share stays stable while searching.
  const totals = useMemo(() => {
    const authors = allAuthors?.authors ?? [];
    return authors.reduce(
      (acc, a) => ({
        views: acc.views + Number(a.view_count),
        app: acc.app + Number(a.app_view_count),
        web: acc.web + Number(a.web_view_count),
        compass: acc.compass + Number(a.compass_view_count),
        unattributed: acc.unattributed + Number(a.unattributed_view_count),
      }),
      { views: 0, app: 0, web: 0, compass: 0, unattributed: 0 }
    );
  }, [allAuthors]);

  const showCompass = totals.compass > 0;
  const showUnattributed = totals.unattributed > 0;

  const num = (v: number | string) => Number(v).toLocaleString();

  const handleExport = () => {
    const headers = [
      'Author',
      'Web impressions (billable)',
      'Web unique users',
      'App views',
      'App share %',
      'App unique users',
      'Compass views',
      'Views with no channel',
      'Total views',
    ];
    const rows = filteredAuthors.map((a) => [
      a.authors,
      Number(a.web_view_count),
      Number(a.web_unique_users),
      Number(a.app_view_count),
      totals.app > 0 ? ((Number(a.app_view_count) / totals.app) * 100).toFixed(2) : '',
      Number(a.app_unique_users),
      Number(a.compass_view_count),
      Number(a.unattributed_view_count),
      Number(a.view_count),
    ]);
    downloadCsv(
      `imray-authors-by-channel_${rangeSuffix(fromDate, toDate)}.csv`,
      toCsv(headers, rows)
    );
  };

  return (
    <div className="space-y-6">
      <InfoPanel id="authors" title="Authors Overview">
        All authors ranked by content views, split by billing channel. <strong>Web</strong> is charged
        per impression, so the web impression count is the billable figure. <strong>App</strong> is
        charged per subscriber, so app views are not a charge in themselves &mdash; the app share
        column shows each author&rsquo;s proportion of app reading, for apportioning subscriber
        revenue. The bar chart ranks the top 20 authors by combined views.
      </InfoPanel>

      <div className="flex justify-between items-start">
        <h1 className="text-2xl font-bold text-gray-900">Authors by Channel</h1>
        <DateRangePicker
          from={fromDate}
          to={toDate}
          onFromChange={setFromDate}
          onToChange={setToDate}
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card">
          <div className="text-sm text-gray-500">Web impressions (billable)</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.web)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">App views (apportionment)</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.app)}</div>
        </div>
        <div className="card">
          <div className="text-sm text-gray-500">Total views</div>
          <div className="text-2xl font-bold text-gray-900">{num(totals.views)}</div>
        </div>
      </div>

      {showUnattributed && (
        <div className="card border-l-4 border-amber-400 bg-amber-50">
          <div className="text-sm text-amber-900">
            <strong>{num(totals.unattributed)}</strong> views in this range have no channel recorded
            and are excluded from the web and app columns. Device tracking began on 2 December 2024;
            earlier events cannot be attributed to web or app. Do not invoice from a range that
            includes them without accounting for this.
          </div>
        </div>
      )}

      {/* All Authors Table */}
      <div className="card">
        <div className="mb-4 flex items-center justify-between gap-4">
          <input
            type="text"
            placeholder="Search authors..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="input max-w-md"
          />
          <button
            type="button"
            onClick={handleExport}
            disabled={filteredAuthors.length === 0}
            className="px-4 py-2 text-sm font-medium rounded-md bg-gray-900 text-white hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed whitespace-nowrap"
          >
            Export CSV
          </button>
        </div>
        <div className="text-sm text-gray-500 mb-2">
          Total: {num(totals.views)} views from {allAuthors?.authors.length || 0} authors
        </div>
        {isLoading ? (
          <div className="space-y-2">
            {[...Array(10)].map((_, i) => (
              <div key={i} className="animate-pulse h-8 bg-gray-200 rounded"></div>
            ))}
          </div>
        ) : (
          <div className="table-container max-h-[600px] overflow-y-auto">
            <table className="table w-full">
              <thead className="sticky top-0">
                <tr>
                  <th className="w-16">#</th>
                  <th>Author</th>
                  <th className="text-right">Web impressions</th>
                  <th className="text-right">Web users</th>
                  <th className="text-right">App views</th>
                  <th className="text-right">App share</th>
                  <th className="text-right">App users</th>
                  {showCompass && <th className="text-right">Compass</th>}
                  {showUnattributed && <th className="text-right">No channel</th>}
                  <th className="text-right">Total</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredAuthors.map((author, index) => (
                  <tr key={index}>
                    <td className="text-gray-500">{index + 1}</td>
                    <td>{author.authors}</td>
                    <td className="text-right font-medium">{num(author.web_view_count)}</td>
                    <td className="text-right text-gray-500">{num(author.web_unique_users)}</td>
                    <td className="text-right font-medium">{num(author.app_view_count)}</td>
                    <td className="text-right text-gray-500">
                      {totals.app > 0
                        ? `${((Number(author.app_view_count) / totals.app) * 100).toFixed(1)}%`
                        : '—'}
                    </td>
                    <td className="text-right text-gray-500">{num(author.app_unique_users)}</td>
                    {showCompass && (
                      <td className="text-right text-gray-500">{num(author.compass_view_count)}</td>
                    )}
                    {showUnattributed && (
                      <td className="text-right text-gray-500">
                        {num(author.unattributed_view_count)}
                      </td>
                    )}
                    <td className="text-right font-medium">{num(author.view_count)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Bar Chart */}
      <div className="card">
        <h2 className="text-lg font-semibold text-gray-700 mb-4">
          Views Distribution (Top 20)
        </h2>
        <div className="h-[600px]">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barChartData}
              layout="vertical"
              margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
            >
              <XAxis type="number" tickFormatter={(value) => value.toLocaleString()} />
              <YAxis
                type="category"
                dataKey="name"
                width={350}
                tick={{ fontSize: 12 }}
              />
              <Tooltip
                formatter={(value: number) => value.toLocaleString()}
                labelFormatter={(_, payload) => payload?.[0]?.payload?.fullName || ''}
              />
              <Bar
                dataKey="value"
                radius={[0, 4, 4, 0]}
                label={{ position: 'right', formatter: (value: number) => value.toLocaleString(), fontSize: 12 }}
              >
                {barChartData.map((_, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
